import React, {useState, useEffect, useRef} from "react";

import {notificationAction} from '../../redux/actions';
import { useSelector, useDispatch } from 'react-redux';
import { notificationSelect } from '../../redux/selectors/index';
import { store } from "../../redux/store";
import {Images} from "./index";
import { 
    RiCloseFill, 
    RiCheckDoubleFill, 
    RiCheckboxCircleFill,
    RiErrorWarningFill,
    RiInformationFill,
    RiCloseLine
} from "react-icons/ri";

let hideIn = () => new Date(Date.now() + 6000);

export const show = (msg, code) => { 
    store.dispatch(notificationAction.onChangeNotification({
        show: true, 
        msg: msg, 
        code: code, 
        hideAfter: hideIn()
    })); 
}

export const hide = () => { 
    store.dispatch(notificationAction.onChangeNotification({show: false})); 
}

let timer = 0;

function Notify() {
    const notificationData = useSelector(notificationSelect);
    const [isVisible, setIsVisible] = useState(false);
    const [progress, setProgress] = useState(100);
    const progressInterval = useRef(null);
    const notificationRef = useRef(null);

    const hideNotification = () => {
        setIsVisible(false);
        setTimeout(() => {
            hide();
            setProgress(100);
        }, 300);
    }

    const getNotificationConfig = (code) => {
        switch(code) {
            case 1: // Success
                return {
                    icon: RiCheckboxCircleFill,
                    color: 'var(--success-500)',
                    bgGradient: 'var(--gradient-success)',
                    iconBg: 'var(--success-100)',
                    textColor: 'var(--success-700)',
                    borderColor: 'var(--success-200)'
                };
            case 0: // Error
                return {
                    icon: RiErrorWarningFill,
                    color: 'var(--error-500)',
                    bgGradient: 'var(--gradient-error)',
                    iconBg: 'var(--error-100)',
                    textColor: 'var(--error-700)',
                    borderColor: 'var(--error-200)'
                };
            default: // Info
                return {
                    icon: RiInformationFill,
                    color: 'var(--primary-500)',
                    bgGradient: 'var(--gradient-primary)',
                    iconBg: 'var(--primary-100)',
                    textColor: 'var(--primary-700)',
                    borderColor: 'var(--primary-200)'
                };
        }
    };

    useEffect(() => {
        clearTimeout(timer);
        clearInterval(progressInterval.current);

        if(notificationData?.show) {
            setIsVisible(true);
            const duration = notificationData?.hideAfter - Date.now();
            
            // Start progress bar countdown
            progressInterval.current = setInterval(() => {
                const remaining = notificationData?.hideAfter - Date.now();
                const percentage = Math.max(0, (remaining / 6000) * 100);
                setProgress(percentage);
                
                if (percentage <= 0) {
                    clearInterval(progressInterval.current);
                }
            }, 50);

            // Auto hide timer
            timer = setTimeout(() => {
                hideNotification();
            }, duration);
        } else {
            setIsVisible(false);
            setProgress(100);
        }

        return () => {
            clearTimeout(timer);
            clearInterval(progressInterval.current);
        };
    }, [notificationData?.show, notificationData?.hideAfter]);

    const config = getNotificationConfig(notificationData?.code);
    const IconComponent = config.icon;

    if(notificationData?.show) {
        return (
            <div 
                className={`notify-overlay ${isVisible ? 'show' : ''}`}
                onClick={hideNotification}
                role="dialog"
                aria-modal="true"
                aria-labelledby="notification-title"
                aria-describedby="notification-message"
            >
                <div 
                    ref={notificationRef}
                    className={`notification-modal ${isVisible ? 'animate-zoom-in' : ''}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        '--notification-color': config.color,
                        '--notification-bg': config.bgGradient,
                        '--notification-icon-bg': config.iconBg,
                        '--notification-text': config.textColor,
                        '--notification-border': config.borderColor
                    }}
                >
                    {/* Progress Bar */}
                    <div className="notification-progress">
                        <div 
                            className="notification-progress-bar"
                            style={{ width: `${progress}%` }}
                        />
                    </div>

                    {/* Close Button */}
                    <button 
                        className="notification-close-btn"
                        onClick={hideNotification}
                        aria-label="Close notification"
                    >
                        <RiCloseLine size={20} />
                    </button>

                    {/* Icon Container */}
                    <div className="notification-icon-container">
                        <div className="notification-icon-wrapper">
                            <IconComponent 
                                size={48} 
                                color={config.color}
                                className="notification-icon"
                            />
                        </div>
                    </div>

                    {/* Content */}
                    <div className="notification-content">
                        <h3 
                            id="notification-title"
                            className="notification-title"
                        >
                            {notificationData?.code === 1 ? 'Success!' : 
                             notificationData?.code === 0 ? 'Error!' : 'Notification'}
                        </h3>
                        <p 
                            id="notification-message"
                            className="notification-message"
                        >
                            {notificationData?.msg}
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="notification-actions">
                        <button 
                            className="notification-action-btn"
                            onClick={hideNotification}
                        >
                            Dismiss
                        </button>
                    </div>
                </div>

                <style jsx>{`
                    .notify-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(0, 0, 0, 0.6);
                        backdrop-filter: blur(8px);
                        -webkit-backdrop-filter: blur(8px);
                        z-index: var(--z-modal);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        opacity: 0;
                        visibility: hidden;
                        transition: all var(--transition-base);
                        padding: var(--space-4);
                    }

                    .notify-overlay.show {
                        opacity: 1;
                        visibility: visible;
                    }

                    .notification-modal {
                        background: rgba(255, 255, 255, 0.95);
                        backdrop-filter: blur(20px);
                        -webkit-backdrop-filter: blur(20px);
                        border: 1px solid var(--notification-border);
                        border-radius: var(--radius-3xl);
                        box-shadow: var(--shadow-2xl);
                        position: relative;
                        max-width: 400px;
                        width: 100%;
                        overflow: hidden;
                        transform: scale(0.8);
                        transition: transform var(--transition-bounce);
                        font-family: var(--font-family-primary);
                    }

                    .notify-overlay.show .notification-modal {
                        transform: scale(1);
                    }

                    .notification-progress {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        height: 4px;
                        background: rgba(0, 0, 0, 0.1);
                        overflow: hidden;
                    }

                    .notification-progress-bar {
                        height: 100%;
                        background: var(--notification-bg);
                        transition: width 50ms linear;
                        border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
                    }

                    .notification-close-btn {
                        position: absolute;
                        top: var(--space-4);
                        right: var(--space-4);
                        background: rgba(0, 0, 0, 0.1);
                        border: none;
                        border-radius: var(--radius-full);
                        width: 32px;
                        height: 32px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all var(--transition-base);
                        color: var(--neutral-600);
                        z-index: 1;
                    }

                    .notification-close-btn:hover {
                        background: rgba(0, 0, 0, 0.2);
                        transform: scale(1.1);
                    }

                    .notification-icon-container {
                        display: flex;
                        justify-content: center;
                        padding: var(--space-8) var(--space-6) var(--space-4);
                    }

                    .notification-icon-wrapper {
                        width: 80px;
                        height: 80px;
                        background: var(--notification-icon-bg);
                        border-radius: var(--radius-full);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        position: relative;
                        box-shadow: var(--shadow-lg);
                    }

                    .notification-icon-wrapper::before {
                        content: '';
                        position: absolute;
                        top: -2px;
                        left: -2px;
                        right: -2px;
                        bottom: -2px;
                        background: var(--notification-bg);
                        border-radius: var(--radius-full);
                        z-index: -1;
                        opacity: 0.3;
                    }

                    .notification-icon {
                        animation: notificationPulse 2s ease-in-out infinite;
                    }

                    @keyframes notificationPulse {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                    }

                    .notification-content {
                        text-align: center;
                        padding: 0 var(--space-6) var(--space-4);
                    }

                    .notification-title {
                        font-size: var(--text-xl);
                        font-weight: var(--font-bold);
                        color: var(--neutral-900);
                        margin: 0 0 var(--space-2) 0;
                        font-family: var(--font-family-secondary);
                    }

                    .notification-message {
                        font-size: var(--text-base);
                        color: var(--neutral-700);
                        line-height: var(--leading-relaxed);
                        margin: 0;
                        font-weight: var(--font-normal);
                    }

                    .notification-actions {
                        padding: 0 var(--space-6) var(--space-6);
                        display: flex;
                        justify-content: center;
                        gap: var(--space-3);
                    }

                    .notification-action-btn {
                        background: var(--notification-bg);
                        color: white;
                        border: none;
                        padding: var(--space-3) var(--space-6);
                        border-radius: var(--radius-lg);
                        font-size: var(--text-sm);
                        font-weight: var(--font-medium);
                        cursor: pointer;
                        transition: all var(--transition-base);
                        font-family: var(--font-family-primary);
                        box-shadow: var(--shadow-md);
                    }

                    .notification-action-btn:hover {
                        transform: translateY(-2px);
                        box-shadow: var(--shadow-lg);
                    }

                    .notification-action-btn:active {
                        transform: translateY(0);
                    }

                    /* Responsive Design */
                    @media (max-width: 640px) {
                        .notification-modal {
                            max-width: calc(100vw - var(--space-8));
                            margin: var(--space-4);
                        }

                        .notification-icon-wrapper {
                            width: 64px;
                            height: 64px;
                        }

                        .notification-icon {
                            width: 36px !important;
                            height: 36px !important;
                        }

                        .notification-title {
                            font-size: var(--text-lg);
                        }

                        .notification-message {
                            font-size: var(--text-sm);
                        }
                    }

                    /* Dark mode support */
                    @media (prefers-color-scheme: dark) {
                        .notification-modal {
                            background: rgba(39, 39, 42, 0.95);
                            border-color: rgba(255, 255, 255, 0.2);
                        }

                        .notification-title {
                            color: var(--dark-900);
                        }

                        .notification-message {
                            color: var(--dark-700);
                        }

                        .notification-close-btn {
                            background: rgba(255, 255, 255, 0.1);
                            color: var(--dark-600);
                        }

                        .notification-close-btn:hover {
                            background: rgba(255, 255, 255, 0.2);
                        }
                    }

                    /* Reduced motion */
                    @media (prefers-reduced-motion: reduce) {
                        .notification-icon {
                            animation: none;
                        }

                        .notification-progress-bar {
                            transition: none;
                        }

                        .notification-modal,
                        .notify-overlay,
                        .notification-close-btn,
                        .notification-action-btn {
                            transition: none;
                        }
                    }
                `}</style>
            </div>
        );
    } else {
        return <></>;
    }
}

export default Notify;